import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

// ============================================================================
// Slot group definitions (inline literals — required for schema extraction)
// ============================================================================
//
// Six curated "flexible living" partner programs (Flex, Obligo, Beagle
// Insurance, RentCafe Rewards, theGuarantors, Pineapple). Each card has:
//   - an image / logo slot group (image + alt text)
//   - a CTA slot group (url + label + target)
// plus a richtext title and body (declared below in `contentSchema.tokens`).
//
// Images default to a property gallery photo so the section always renders
// something on fresh properties; operators replace them with partner logos.

// Default images are the partner's own brand logo (via Clearbit's public logo
// endpoint, keyed off each partner's domain). Operators can swap the slot for
// a proper partner-supplied asset in the editor — the alt text is kept
// descriptive so screen readers still work if the fallback logo breaks.
const card1ImageDef: SlotGroupDefinition = {
  label: 'Card 1 Logo',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: 'https://logo.clearbit.com/getflex.com?size=400',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Flex logo',
    },
  },
};

const card1CtaDef: SlotGroupDefinition = {
  label: 'Card 1 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.getflex.com',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Go to Flex',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Card 2 Logo',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: 'https://logo.clearbit.com/obligo.com?size=400',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Obligo logo',
    },
  },
};

const card2CtaDef: SlotGroupDefinition = {
  label: 'Card 2 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.obligo.com/',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Go to Obligo',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Card 3 Logo',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: 'https://logo.clearbit.com/beaglenotice.com?size=400',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Beagle Insurance logo',
    },
  },
};

const card3CtaDef: SlotGroupDefinition = {
  label: 'Card 3 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.beaglenotice.com/',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Go to Beagle Insurance',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const card4ImageDef: SlotGroupDefinition = {
  label: 'Card 4 Logo',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: 'https://logo.clearbit.com/rentcafe.com?size=400',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'RentCafe Rewards logo',
    },
  },
};

const card4CtaDef: SlotGroupDefinition = {
  label: 'Card 4 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.rentcafe.com/living/rewards/',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Go to Rewards by RentCafe',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const card5ImageDef: SlotGroupDefinition = {
  label: 'Card 5 Logo',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: 'https://logo.clearbit.com/theguarantors.com?size=400',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'theGuarantors logo',
    },
  },
};

const card5CtaDef: SlotGroupDefinition = {
  label: 'Card 5 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.theguarantors.com/renter',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Go to theGuarantors',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const card6ImageDef: SlotGroupDefinition = {
  label: 'Card 6 Logo',
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: 'https://logo.clearbit.com/tourwithpineapple.com?size=400',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Pineapple logo',
    },
  },
};

const card6CtaDef: SlotGroupDefinition = {
  label: 'Card 6 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://tourwithpineapple.com/',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Go to Pineapple',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Reusable chevron arrow for every card CTA
// ============================================================================

function ArrowIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function FlexibleLivingPrograms() {
  useRevealStyles();
  const card1Ref = useScrollReveal<HTMLDivElement>();
  const card2Ref = useScrollReveal<HTMLDivElement>();
  const card3Ref = useScrollReveal<HTMLDivElement>();
  const card4Ref = useScrollReveal<HTMLDivElement>();
  const card5Ref = useScrollReveal<HTMLDivElement>();
  const card6Ref = useScrollReveal<HTMLDivElement>();

  const card1Image = useSlotGroup('flexiblelivingprograms.card1_image', card1ImageDef);
  const card1Cta = useSlotGroup('flexiblelivingprograms.card1_cta', card1CtaDef);
  const card2Image = useSlotGroup('flexiblelivingprograms.card2_image', card2ImageDef);
  const card2Cta = useSlotGroup('flexiblelivingprograms.card2_cta', card2CtaDef);
  const card3Image = useSlotGroup('flexiblelivingprograms.card3_image', card3ImageDef);
  const card3Cta = useSlotGroup('flexiblelivingprograms.card3_cta', card3CtaDef);
  const card4Image = useSlotGroup('flexiblelivingprograms.card4_image', card4ImageDef);
  const card4Cta = useSlotGroup('flexiblelivingprograms.card4_cta', card4CtaDef);
  const card5Image = useSlotGroup('flexiblelivingprograms.card5_image', card5ImageDef);
  const card5Cta = useSlotGroup('flexiblelivingprograms.card5_cta', card5CtaDef);
  const card6Image = useSlotGroup('flexiblelivingprograms.card6_image', card6ImageDef);
  const card6Cta = useSlotGroup('flexiblelivingprograms.card6_cta', card6CtaDef);
  const ctaColorVars = useCtaColorVars();

  return (
    <section className="flp-section" style={ctaColorVars}>
      <div className="flp-container">
        <div className="flp-grid">
          {/* Card 1 — Split Payments (Flex) */}
          <article ref={card1Ref} className="reveal-fade-up flp-card">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card1_image" definition={card1ImageDef}>
              <div className="flp-card-image-wrap">
                {card1Image.image ? (
                  <VibesitesImage
                    url={card1Image.image}
                    alt={card1Image.alt || ''}
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    className="flp-card-image"
                  />
                ) : (
                  <div className="flp-card-image flp-card-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>
            <div className="flp-card-body">
              <RichTextContent
                path="flexiblelivingprograms.card1_title"
                label="Card 1 Title"
                defaultValue="<p>Split Payments Easily</p>"
                as="h3"
                className="flp-card-title"
              />
              <RichTextContent
                path="flexiblelivingprograms.card1_body"
                label="Card 1 Body"
                defaultValue="<p>Split your rent into two payments that match your pay schedule. Pay Flex first, and they pay your full rent on time.</p>"
                as="div"
                className="flp-card-text"
              />
              <EditableSlotGroup groupPath="flexiblelivingprograms.card1_cta" definition={card1CtaDef}>
                <a
                  href={resolveHref(card1Cta.url || '#')}
                  target={card1Cta._target || '_blank'}
                  rel={card1Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="flp-card-cta"
                >
                  <span>{card1Cta.label || 'Learn More'}</span>
                  <ArrowIcon />
                </a>
              </EditableSlotGroup>
            </div>
          </article>

          {/* Card 2 — Flexible Deposits (Obligo) */}
          <article ref={card2Ref} className="reveal-fade-up flp-card">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card2_image" definition={card2ImageDef}>
              <div className="flp-card-image-wrap">
                {card2Image.image ? (
                  <VibesitesImage
                    url={card2Image.image}
                    alt={card2Image.alt || ''}
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    className="flp-card-image"
                  />
                ) : (
                  <div className="flp-card-image flp-card-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>
            <div className="flp-card-body">
              <RichTextContent
                path="flexiblelivingprograms.card2_title"
                label="Card 2 Title"
                defaultValue="<p>Flexible Deposits</p>"
                as="h3"
                className="flp-card-title"
              />
              <RichTextContent
                path="flexiblelivingprograms.card2_body"
                label="Card 2 Body"
                defaultValue="<p>Reduce upfront move-in costs with flexible deposit options. Handle everything from your phone — no checks or waiting.</p>"
                as="div"
                className="flp-card-text"
              />
              <EditableSlotGroup groupPath="flexiblelivingprograms.card2_cta" definition={card2CtaDef}>
                <a
                  href={resolveHref(card2Cta.url || '#')}
                  target={card2Cta._target || '_blank'}
                  rel={card2Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="flp-card-cta"
                >
                  <span>{card2Cta.label || 'Learn More'}</span>
                  <ArrowIcon />
                </a>
              </EditableSlotGroup>
            </div>
          </article>

          {/* Card 3 — Insurance (Beagle) */}
          <article ref={card3Ref} className="reveal-fade-up flp-card">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card3_image" definition={card3ImageDef}>
              <div className="flp-card-image-wrap">
                {card3Image.image ? (
                  <VibesitesImage
                    url={card3Image.image}
                    alt={card3Image.alt || ''}
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    className="flp-card-image"
                  />
                ) : (
                  <div className="flp-card-image flp-card-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>
            <div className="flp-card-body">
              <RichTextContent
                path="flexiblelivingprograms.card3_title"
                label="Card 3 Title"
                defaultValue="<p>Insurance Done Right</p>"
                as="h3"
                className="flp-card-title"
              />
              <RichTextContent
                path="flexiblelivingprograms.card3_body"
                label="Card 3 Body"
                defaultValue="<p>Get a quick quote and enroll directly in your application or resident portal. Covers required liability with optional personal property protection.</p>"
                as="div"
                className="flp-card-text"
              />
              <EditableSlotGroup groupPath="flexiblelivingprograms.card3_cta" definition={card3CtaDef}>
                <a
                  href={resolveHref(card3Cta.url || '#')}
                  target={card3Cta._target || '_blank'}
                  rel={card3Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="flp-card-cta"
                >
                  <span>{card3Cta.label || 'Learn More'}</span>
                  <ArrowIcon />
                </a>
              </EditableSlotGroup>
            </div>
          </article>

          {/* Card 4 — Rewards (RentCafe) */}
          <article ref={card4Ref} className="reveal-fade-up flp-card">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card4_image" definition={card4ImageDef}>
              <div className="flp-card-image-wrap">
                {card4Image.image ? (
                  <VibesitesImage
                    url={card4Image.image}
                    alt={card4Image.alt || ''}
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    className="flp-card-image"
                  />
                ) : (
                  <div className="flp-card-image flp-card-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>
            <div className="flp-card-body">
              <RichTextContent
                path="flexiblelivingprograms.card4_title"
                label="Card 4 Title"
                defaultValue="<p>Rewards Program</p>"
                as="h3"
                className="flp-card-title"
              />
              <RichTextContent
                path="flexiblelivingprograms.card4_body"
                label="Card 4 Body"
                defaultValue="<p>Earn points for essentials like renters insurance and on-time rent payments. Redeem for gift cards, dining, and more.</p>"
                as="div"
                className="flp-card-text"
              />
              <EditableSlotGroup groupPath="flexiblelivingprograms.card4_cta" definition={card4CtaDef}>
                <a
                  href={resolveHref(card4Cta.url || '#')}
                  target={card4Cta._target || '_blank'}
                  rel={card4Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="flp-card-cta"
                >
                  <span>{card4Cta.label || 'Learn More'}</span>
                  <ArrowIcon />
                </a>
              </EditableSlotGroup>
            </div>
          </article>

          {/* Card 5 — Lease Guarantee (theGuarantors) */}
          <article ref={card5Ref} className="reveal-fade-up flp-card">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card5_image" definition={card5ImageDef}>
              <div className="flp-card-image-wrap">
                {card5Image.image ? (
                  <VibesitesImage
                    url={card5Image.image}
                    alt={card5Image.alt || ''}
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    className="flp-card-image"
                  />
                ) : (
                  <div className="flp-card-image flp-card-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>
            <div className="flp-card-body">
              <RichTextContent
                path="flexiblelivingprograms.card5_title"
                label="Card 5 Title"
                defaultValue="<p>Lease Guarantee</p>"
                as="h3"
                className="flp-card-title"
              />
              <RichTextContent
                path="flexiblelivingprograms.card5_body"
                label="Card 5 Body"
                defaultValue="<p>Qualify even if you're self-employed, new to renting, or don't meet standard requirements with a trusted lease guarantor.</p>"
                as="div"
                className="flp-card-text"
              />
              <EditableSlotGroup groupPath="flexiblelivingprograms.card5_cta" definition={card5CtaDef}>
                <a
                  href={resolveHref(card5Cta.url || '#')}
                  target={card5Cta._target || '_blank'}
                  rel={card5Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="flp-card-cta"
                >
                  <span>{card5Cta.label || 'Learn More'}</span>
                  <ArrowIcon />
                </a>
              </EditableSlotGroup>
            </div>
          </article>

          {/* Card 6 — Self-Guided Tours (Pineapple) */}
          <article ref={card6Ref} className="reveal-fade-up flp-card">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card6_image" definition={card6ImageDef}>
              <div className="flp-card-image-wrap">
                {card6Image.image ? (
                  <VibesitesImage
                    url={card6Image.image}
                    alt={card6Image.alt || ''}
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    className="flp-card-image"
                  />
                ) : (
                  <div className="flp-card-image flp-card-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>
            <div className="flp-card-body">
              <RichTextContent
                path="flexiblelivingprograms.card6_title"
                label="Card 6 Title"
                defaultValue="<p>Self-Guided Tours</p>"
                as="h3"
                className="flp-card-title"
              />
              <RichTextContent
                path="flexiblelivingprograms.card6_body"
                label="Card 6 Body"
                defaultValue="<p>Tour on your schedule with our self-guided touring platform. Explore available apartment homes at your own pace.</p>"
                as="div"
                className="flp-card-text"
              />
              <EditableSlotGroup groupPath="flexiblelivingprograms.card6_cta" definition={card6CtaDef}>
                <a
                  href={resolveHref(card6Cta.url || '#')}
                  target={card6Cta._target || '_blank'}
                  rel={card6Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="flp-card-cta"
                >
                  <span>{card6Cta.label || 'Learn More'}</span>
                  <ArrowIcon />
                </a>
              </EditableSlotGroup>
            </div>
          </article>
        </div>

        <RichTextContent
          path="flexiblelivingprograms.disclaimer"
          label="Disclaimer"
          defaultValue="<p>Third-party programs are provided by independent companies. Program availability, eligibility, pricing, and terms are set by each provider and may change without notice.</p>"
          as="div"
          className="flp-disclaimer"
        />
      </div>

      <style>{`
        .flp-section {
          position: relative;
          background: var(--vibe-color-background, #ffffff);
          /* Tight top + bottom pad — the preceding HeroSolidBG already
             finishes with its own chevron+accent, and the following
             WhiteStripe owns its own vertical rhythm, so this section
             shouldn't add another big empty block on either edge. */
          padding: 1.5rem 1.25rem 2rem;
        }
        @media (min-width: 768px) {
          .flp-section { padding: 2.5rem 1.5rem 2.5rem; }
        }

        .flp-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        /* 3-up on desktop, 2-up on tablet, stacked on phones. */
        .flp-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.75rem;
        }
        @media (min-width: 640px) {
          .flp-grid {
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
          }
        }
        @media (min-width: 1024px) {
          .flp-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 2.25rem;
          }
        }

        /* Card: white body, subtle shadow, brand accent stripe at bottom. */
        .flp-card {
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.06);
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          position: relative;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .flp-card::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 3px;
          background: var(--cta-bg, var(--vibe-color-secondary, #3E441D));
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .flp-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
        }
        .flp-card:hover::after {
          transform: scaleX(1);
        }

        /* Logos sit inside a fixed-ratio frame with equal padding so every
           card's mark renders uncropped and at a consistent size. */
        .flp-card-image-wrap {
          position: relative;
          aspect-ratio: 5 / 3;
          background: #ffffff;
          overflow: hidden;
        }
        .flp-card-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          padding: 1.5rem 2rem;
          box-sizing: border-box;
          display: block;
          transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .flp-card:hover .flp-card-image {
          transform: scale(1.04);
        }
        .flp-card-image--placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(
            135deg,
            var(--vibe-color-surface, #f3f1ea) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 10%, #ffffff) 100%
          );
        }

        .flp-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          padding: 1.5rem 1.5rem 1.75rem;
          flex: 1;
        }

        .flp-card-title,
        .flp-card-title * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        .flp-card-title {
          font-size: 1.375rem;
          line-height: 1.2;
          margin: 0;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-secondary, #3E441D);
        }

        .flp-card-text,
        .flp-card-text * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .flp-card-text {
          font-size: 0.9375rem;
          line-height: 1.55;
          color: var(--vibe-color-text, #342E1F);
          flex: 1;
        }
        .flp-card-text p { margin: 0 0 0.6rem; }
        .flp-card-text p:last-child { margin-bottom: 0; }

        .flp-card-cta {
          align-self: flex-start;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.4rem;
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-size: 0.8125rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--vibe-color-secondary, #3E441D);
          text-decoration: none;
          border-bottom: 1px solid currentColor;
          padding: 0.25rem 0;
          transition: color 0.25s ease, gap 0.25s ease;
        }
        .flp-card-cta:hover {
          color: var(--vibe-color-primary);
          gap: 0.75rem;
        }
        .flp-card-cta svg {
          transition: transform 0.25s ease;
        }
        .flp-card-cta:hover svg {
          transform: translateX(2px);
        }

        .flp-disclaimer,
        .flp-disclaimer * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .flp-disclaimer {
          margin: 2rem auto 0;
          max-width: 820px;
          font-size: 0.8125rem;
          line-height: 1.55;
          color: color-mix(in srgb, var(--vibe-color-text) 70%, transparent);
          text-align: center;
        }
        .flp-disclaimer p { margin: 0; }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FlexibleLivingPrograms',
  displayName: 'Flexible Living Programs',
  tokens: {
    'flexiblelivingprograms.card1_title': {
      label: 'Card 1 Title',
      type: 'richtext',
      defaultValue: '<p>Split Payments Easily</p>',
    },
    'flexiblelivingprograms.card1_body': {
      label: 'Card 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Split your rent into two payments that match your pay schedule. Pay Flex first, and they pay your full rent on time.</p>',
    },
    'flexiblelivingprograms.card2_title': {
      label: 'Card 2 Title',
      type: 'richtext',
      defaultValue: '<p>Flexible Deposits</p>',
    },
    'flexiblelivingprograms.card2_body': {
      label: 'Card 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Reduce upfront move-in costs with flexible deposit options. Handle everything from your phone — no checks or waiting.</p>',
    },
    'flexiblelivingprograms.card3_title': {
      label: 'Card 3 Title',
      type: 'richtext',
      defaultValue: '<p>Insurance Done Right</p>',
    },
    'flexiblelivingprograms.card3_body': {
      label: 'Card 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Get a quick quote and enroll directly in your application or resident portal. Covers required liability with optional personal property protection.</p>',
    },
    'flexiblelivingprograms.card4_title': {
      label: 'Card 4 Title',
      type: 'richtext',
      defaultValue: '<p>Rewards Program</p>',
    },
    'flexiblelivingprograms.card4_body': {
      label: 'Card 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>Earn points for essentials like renters insurance and on-time rent payments. Redeem for gift cards, dining, and more.</p>',
    },
    'flexiblelivingprograms.card5_title': {
      label: 'Card 5 Title',
      type: 'richtext',
      defaultValue: '<p>Lease Guarantee</p>',
    },
    'flexiblelivingprograms.card5_body': {
      label: 'Card 5 Body',
      type: 'richtext',
      defaultValue:
        "<p>Qualify even if you're self-employed, new to renting, or don't meet standard requirements with a trusted lease guarantor.</p>",
    },
    'flexiblelivingprograms.card6_title': {
      label: 'Card 6 Title',
      type: 'richtext',
      defaultValue: '<p>Self-Guided Tours</p>',
    },
    'flexiblelivingprograms.card6_body': {
      label: 'Card 6 Body',
      type: 'richtext',
      defaultValue:
        '<p>Tour on your schedule with our self-guided touring platform. Explore available apartment homes at your own pace.</p>',
    },
    'flexiblelivingprograms.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue:
        '<p>Third-party programs are provided by independent companies. Program availability, eligibility, pricing, and terms are set by each provider and may change without notice.</p>',
    },
  },
  slotGroups: {
    'flexiblelivingprograms.card1_image': card1ImageDef,
    'flexiblelivingprograms.card1_cta': card1CtaDef,
    'flexiblelivingprograms.card2_image': card2ImageDef,
    'flexiblelivingprograms.card2_cta': card2CtaDef,
    'flexiblelivingprograms.card3_image': card3ImageDef,
    'flexiblelivingprograms.card3_cta': card3CtaDef,
    'flexiblelivingprograms.card4_image': card4ImageDef,
    'flexiblelivingprograms.card4_cta': card4CtaDef,
    'flexiblelivingprograms.card5_image': card5ImageDef,
    'flexiblelivingprograms.card5_cta': card5CtaDef,
    'flexiblelivingprograms.card6_image': card6ImageDef,
    'flexiblelivingprograms.card6_cta': card6CtaDef,
  },
};

(FlexibleLivingPrograms as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FlexibleLivingPrograms;
